import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { liveQuery } from 'dexie';
import { motion } from 'framer-motion';
import { conversationRepo } from '../../core/repositories';
import type { Conversation } from '../../core/types';
import { useMotionPresets } from '../../design/motion';
import { AppIcon, useComponentMeta } from '../../ui';
import { chatComponentMeta } from './componentMeta';
import styles from './RoleSessions.module.css';

export function RoleSessions({ conversation, title, onClose, onOpen }: {
  conversation: Conversation;
  title: string;
  onClose: () => void;
  onOpen: (conversation: Conversation) => void;
}): ReactElement {
  const presets = useMotionPresets();
  const [sessions, setSessions] = useState<Conversation[]>([]);
  const [error, setError] = useState(false);
  const dialogRef = useComponentMeta<HTMLElement>(chatComponentMeta('chat.sessions.dialog'));
  const closeRef = useComponentMeta<HTMLButtonElement>(chatComponentMeta('chat.sessions.close'));
  const closeHandler = useRef(onClose);
  closeHandler.current = onClose;
  useEffect(() => {
    const subscription = liveQuery(async () => (await conversationRepo.listConversations()).filter(item =>
      conversation.presetId ? item.presetId === conversation.presetId : !item.presetId,
    )).subscribe({ next: setSessions, error: () => setError(true) });
    return () => subscription.unsubscribe();
  }, [conversation.presetId]);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const keydown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') { event.preventDefault(); closeHandler.current(); }
      if (event.key !== 'Tab') return;
      const targets = dialogRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
      const first = targets?.[0];
      const last = targets?.[targets.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => { document.removeEventListener('keydown', keydown); previous?.focus(); };
  }, [closeRef, dialogRef]);
  return <>
    <motion.div className={styles.backdrop} variants={presets.sheetBackdrop} initial="hidden" animate="visible" exit="hidden" onClick={onClose} />
    <motion.section ref={dialogRef} className={styles.dialog} data-ui-id="chat.sessions.dialog" data-skin-surface="overlay" role="dialog" aria-modal="true" aria-label={`${title}的会话列表`} variants={presets.sheet} initial="hidden" animate="visible" exit="hidden">
      <div className={styles.header}>
        <h2>{title} · 会话</h2>
        <button ref={closeRef} data-ui-id="chat.sessions.close" type="button" aria-label="关闭会话列表" title="关闭会话列表" onClick={onClose}><AppIcon name="close" size={20} /></button>
      </div>
      <div className={styles.list}>
        {error ? <p role="alert">读取会话失败，请关闭后重试</p> : sessions.map(item => <button key={item.id} type="button" className={styles.item} aria-current={item.id === conversation.id ? 'true' : undefined} onClick={() => onOpen(item)}>
          <AppIcon name="conversations" size={20} />
          <span><strong>{item.title}</strong><small>{new Date(item.updatedAt).toLocaleString()} · {item.messageCount} 条消息</small></span>
          {item.id === conversation.id ? <AppIcon name="check" size={18} /> : null}
        </button>)}
      </div>
    </motion.section>
  </>;
}
